import { useMutation, useQueryClient } from '@tanstack/react-query';

import { constructStoreItemFormDataConfig } from '../../utils/constructStoreItemFormDataConfig';
import { StoreApi } from '../api';
import { storeKeys } from '../query-key-factory';
import { StoreItem, StoreItemResponse } from '../types';

// update StoreItem; generic
type UpdateStoreItemRequest = {
  causeId: string;
  store_item: Partial<StoreItem> & { files?: File[] };
  uuid: string;
};
interface UpdateStoreItemAccountCodeRequest extends Omit<UpdateStoreItemRequest, 'store_item'> {
  account_code: StoreItem['account_code'];
}

type UpdateBulkAssignStoreItemAccountCodesRequest = {
  causeId: string;
  accountCode?: StoreItem['account_code'];
  storeItemId?: string;
};

const useUpdateStoreItem = () => {
  const queryClient = useQueryClient();

  const updateStoreItemWithFiles = async ({
    causeId,
    store_item,
    uuid,
  }: UpdateStoreItemRequest) => {
    const { data } = await StoreApi.put<StoreItemResponse<StoreItem>>(
      `/store_items/${uuid}`,
      {
        cause_id: causeId,
        store_item,
        uuid,
      },
      constructStoreItemFormDataConfig(store_item?.files)
    );

    return data;
  };

  return useMutation({
    mutationFn: updateStoreItemWithFiles,
    onSuccess(_, variables) {
      const { causeId } = variables;
      const key = storeKeys.id(causeId).items.all;
      void queryClient.invalidateQueries({ queryKey: key });
    },
  });
};

// update StoreItem account_code
const useUpdateStoreItemAccountCode = () => {
  const updateAccountCode = async ({
    causeId,
    account_code,
    uuid,
  }: UpdateStoreItemAccountCodeRequest): Promise<StoreItem> => {
    const { data } = await StoreApi.put(`/store_items/${uuid}`, {
      cause_id: causeId,
      store_item: { account_code },
      uuid,
    });
    return data;
  };

  return useMutation({
    mutationFn: updateAccountCode,
  });
};

const useUpdateStoreItemAccountCodes = () => {
  const updateAccountCode = async ({
    causeId,
    accountCode,
    storeItemId,
  }: UpdateBulkAssignStoreItemAccountCodesRequest): Promise<StoreItem[]> => {
    const { data } = await StoreApi.put(`/store_items/account_codes/assign`, {
      cause_id: causeId,
      store_item_ids: [storeItemId ?? ''],
      account_code: accountCode,
    });
    return data;
  };

  return useMutation({
    mutationFn: updateAccountCode,
  });
};

type PublishStoreItemRequest = {
  causeId: string;
  uuid: string;
  store_item: {
    /**
     * If supplied, the item will be published at the given date and time.
     * If null, the item will be published immediately.
     */
    starting_at: string | null;
    /**
     * If supplied, the item will be unpublished at the given date and time.
     * If null, the item will remain published indefinitely.
     */
    expires_at: string | null;
    in_app?: boolean;
    in_store?: boolean;
    private_listing?: boolean;
  };
};

const usePublishStoreItem = () => {
  const queryClient = useQueryClient();

  const publishStoreItem = async ({
    causeId,
    uuid,
    store_item,
  }: PublishStoreItemRequest): Promise<StoreItem> => {
    const { data } = await StoreApi.put<StoreItem>(`/store_items/${uuid}/publish`, {
      cause_id: causeId,
      store_item,
    });

    return data;
  };

  return useMutation({
    mutationFn: publishStoreItem,
    onSuccess: (_, variables) => {
      const { causeId, uuid } = variables;
      const key = storeKeys.id(causeId).items.id(uuid).detail;
      void queryClient.invalidateQueries({ queryKey: key });
    },
  });
};

type UnpublishStoreItemRequest = {
  causeId: string;
  uuid: string;
};

const useUnpublishStoreItem = () => {
  const queryClient = useQueryClient();
  const unpublishStoreItem = async ({
    causeId,
    uuid,
  }: UnpublishStoreItemRequest): Promise<StoreItem> => {
    const { data } = await StoreApi.put<StoreItem>(`/store_items/${uuid}/unpublish`, {
      cause_id: causeId,
    });

    return data;
  };

  return useMutation({
    mutationFn: unpublishStoreItem,
    onSuccess: (_, variables) => {
      const { causeId, uuid } = variables;
      const key = storeKeys.id(causeId).items.id(uuid).detail;
      void queryClient.invalidateQueries({ queryKey: key });
    },
  });
};

type BulkUnarchiveStoreItemRequest = {
  storeItemIds: string[];
  causeId: string;
};

const useBulkUnarchiveStoreItem = () => {
  const bulkUnarchiveStoreItem = async ({
    storeItemIds,
    causeId,
  }: BulkUnarchiveStoreItemRequest) => {
    return StoreApi.put('/store_items/bulk/unarchive', {
      store_item_ids: storeItemIds,
      cause_id: causeId,
    });
  };
  return useMutation({
    mutationFn: bulkUnarchiveStoreItem,
  });
};

type BulkArchiveStoreItemRequest = {
  storeItemIds: string[];
  causeId: string;
};

const useBulkArchiveStoreItem = () => {
  const bulkArchiveStoreItem = async ({
    storeItemIds,
    causeId,
  }: BulkArchiveStoreItemRequest): Promise<StoreItem[]> => {
    const { data } = await StoreApi.put('/store_items/bulk/archive', {
      store_item_ids: storeItemIds,
      cause_id: causeId,
    });
    return data;
  };

  return useMutation({
    mutationFn: bulkArchiveStoreItem,
  });
};

type BulkPublishStoreItemRequest = {
  storeItemIds: string[];
  causeId: string;
  startingAt: string;
  expiresAt: string;
  inApp: boolean;
  inStore: boolean;
  privateListing: boolean;
};

const useBulkPublishStoreItem = () => {
  const bulkPublishStoreItems = async ({
    storeItemIds,
    causeId,
    startingAt,
    expiresAt,
    inApp,
    inStore,
    privateListing,
  }: BulkPublishStoreItemRequest): Promise<StoreItem[]> => {
    const { data } = await StoreApi.put('/store_items/bulk/publish', {
      store_item_ids: storeItemIds,
      cause_id: causeId,
      starting_at: startingAt,
      expires_at: expiresAt,
      in_app: inApp,
      in_store: inStore,
      private_listing: privateListing,
    });
    return data;
  };

  return useMutation({
    mutationFn: bulkPublishStoreItems,
  });
};

type BulkUnpublishStoreItemRequest = {
  storeItemIds: string[];
  causeId: string;
  expiresAt: string;
};

const useBulkUnpublishStoreItem = () => {
  const bulkUnpublishStoreItems = async ({
    storeItemIds,
    causeId,
    expiresAt,
  }: BulkUnpublishStoreItemRequest): Promise<StoreItem[]> => {
    const { data } = await StoreApi.put('/store_items/bulk/unpublish', {
      store_item_ids: storeItemIds,
      cause_id: causeId,
      expires_at: expiresAt,
    });
    return data;
  };

  return useMutation({
    mutationFn: bulkUnpublishStoreItems,
  });
};

export {
  useBulkArchiveStoreItem,
  useBulkUnarchiveStoreItem,
  useBulkPublishStoreItem,
  useBulkUnpublishStoreItem,
  usePublishStoreItem,
  useUnpublishStoreItem,
  useUpdateStoreItem,
  useUpdateStoreItemAccountCode,
  useUpdateStoreItemAccountCodes,
};
